import { useMemo } from "react";

import type { ChainDetail } from "@/api";

/**
 * Gantt-style chain timeline.
 *
 * For each leg we render a horizontal bar from `pickup_at` to `drop_at`,
 * split into a "drive" segment (blue, `drive_time_min` minutes from
 * pickup) and a "drop wait" tail (slate). Between legs the inter-load
 * slack appears as a gap. A simple time axis sits at the top with a tick
 * every ~3 hours.
 *
 * Pure SVG + Tailwind — no charting lib. Self-contained at ~120 LOC.
 */

const ROW_HEIGHT = 36;
const ROW_GAP = 8;
const AXIS_HEIGHT = 28;
const LEFT_GUTTER = 110;
const RIGHT_PAD = 16;

interface TimelineProps {
  chain: ChainDetail;
}

export function Timeline({ chain }: TimelineProps): JSX.Element {
  const data = useMemo(() => buildModel(chain), [chain]);

  if (data.legs.length === 0) {
    return (
      <div className="card p-6 text-center text-slate-500">
        No legs in this chain.
      </div>
    );
  }

  const innerWidth = 800;                                  // logical SVG width
  const plotWidth = innerWidth - LEFT_GUTTER - RIGHT_PAD;
  const totalHeight =
    AXIS_HEIGHT + data.legs.length * (ROW_HEIGHT + ROW_GAP);

  const toX = (ms: number) =>
    LEFT_GUTTER +
    ((ms - data.startMs) / (data.endMs - data.startMs)) * plotWidth;

  return (
    <div className="card p-4 overflow-x-auto">
      <div className="flex items-center gap-4 mb-3 text-xs">
        <Legend swatch="bg-brand-600" label="drive" />
        <Legend swatch="bg-slate-300" label="drop wait / slack" />
      </div>
      <svg
        role="img"
        aria-label="Chain timeline"
        viewBox={`0 0 ${innerWidth} ${totalHeight}`}
        className="w-full"
        style={{ minWidth: 560 }}
      >
        <Axis startMs={data.startMs} endMs={data.endMs} toX={toX} />
        {data.legs.map((leg, idx) => (
          <LegRow
            key={`${leg.seq}-${leg.load_id}`}
            leg={leg}
            idx={idx}
            toX={toX}
          />
        ))}
      </svg>
    </div>
  );
}

// ─────────────────────────────────  Subcomponents  ──

interface LegRowProps {
  leg: ReturnType<typeof buildModel>["legs"][number];
  idx: number;
  toX: (ms: number) => number;
}

function LegRow({ leg, idx, toX }: LegRowProps): JSX.Element {
  const y = AXIS_HEIGHT + idx * (ROW_HEIGHT + ROW_GAP);
  const driveEnd = leg.pickupMs + leg.driveMs;
  const x1 = toX(leg.pickupMs);
  const x2 = toX(driveEnd);
  const x3 = toX(leg.dropMs);

  return (
    <g>
      {/* label gutter */}
      <text
        x={LEFT_GUTTER - 8}
        y={y + ROW_HEIGHT / 2 + 4}
        textAnchor="end"
        fontSize="11"
        fill="#475569"
      >
        load #{leg.load_id}
      </text>
      {/* drive segment */}
      <rect
        x={x1}
        y={y}
        width={Math.max(2, x2 - x1)}
        height={ROW_HEIGHT}
        rx={4}
        className="fill-brand-600"
      >
        <title>
          drive: {fmtMin(leg.driveMs / 60_000)} (
          {new Date(leg.pickupMs).toLocaleString()} →{" "}
          {new Date(driveEnd).toLocaleString()})
        </title>
      </rect>
      {/* drop-wait tail (if drop_at after drive end) */}
      {x3 > x2 && (
        <rect
          x={x2}
          y={y + ROW_HEIGHT * 0.25}
          width={x3 - x2}
          height={ROW_HEIGHT * 0.5}
          className="fill-slate-300"
        >
          <title>
            wait until drop: {fmtMin((leg.dropMs - driveEnd) / 60_000)}
          </title>
        </rect>
      )}
    </g>
  );
}

interface AxisProps {
  startMs: number;
  endMs: number;
  toX: (ms: number) => number;
}

function Axis({ startMs, endMs, toX }: AxisProps): JSX.Element {
  const ticks = useMemo(() => buildAxisTicks(startMs, endMs), [startMs, endMs]);
  return (
    <g>
      <line
        x1={LEFT_GUTTER}
        y1={AXIS_HEIGHT - 4}
        x2={toX(endMs)}
        y2={AXIS_HEIGHT - 4}
        stroke="#cbd5e1"
        strokeWidth={1}
      />
      {ticks.map((t) => (
        <g key={t.ms}>
          <line
            x1={toX(t.ms)}
            y1={AXIS_HEIGHT - 8}
            x2={toX(t.ms)}
            y2={AXIS_HEIGHT - 4}
            stroke="#94a3b8"
          />
          <text
            x={toX(t.ms)}
            y={AXIS_HEIGHT - 12}
            textAnchor="middle"
            fontSize="10"
            fill="#64748b"
          >
            {t.label}
          </text>
        </g>
      ))}
    </g>
  );
}

function Legend({ swatch, label }: { swatch: string; label: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 text-slate-600">
      <span className={`inline-block w-3 h-3 rounded-sm ${swatch}`} />
      {label}
    </span>
  );
}

// ─────────────────────────────────  Pure helpers  ──

interface LegModel {
  seq: number;
  load_id: number;
  pickupMs: number;
  dropMs: number;
  driveMs: number;
}

interface TimelineModel {
  legs: LegModel[];
  startMs: number;
  endMs: number;
}

export function buildModel(chain: ChainDetail): TimelineModel {
  const legs: LegModel[] = chain.loads.map((l) => ({
    seq: l.seq,
    load_id: l.load_id,
    pickupMs: Date.parse(l.pickup_at),
    dropMs: Date.parse(l.drop_at),
    driveMs: l.drive_time_min * 60_000,
  }));
  if (legs.length === 0) return { legs: [], startMs: 0, endMs: 1 };

  const startMs = Math.min(...legs.map((l) => l.pickupMs));
  const endMs = Math.max(...legs.map((l) => l.dropMs));
  return { legs, startMs, endMs: Math.max(endMs, startMs + 60_000) };
}

export function buildAxisTicks(
  startMs: number,
  endMs: number,
): { ms: number; label: string }[] {
  const spanH = (endMs - startMs) / 3_600_000;
  const stepH = spanH > 36 ? 6 : spanH > 12 ? 3 : 1;
  const stepMs = stepH * 3_600_000;
  // Snap to the start of the hour.
  const firstMs = Math.ceil(startMs / stepMs) * stepMs;
  const out: { ms: number; label: string }[] = [];
  for (let m = firstMs; m <= endMs; m += stepMs) {
    out.push({
      ms: m,
      label: new Date(m).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
      }),
    });
  }
  return out;
}

function fmtMin(min: number): string {
  if (min < 60) return `${Math.round(min)} min`;
  return `${(min / 60).toFixed(1)} h`;
}
